// src/pages/AdminSellers.jsx
import { useEffect, useMemo, useState } from "react";
import { auth, db } from "../firebase/firebase.js";
import {
  collection,
  doc,
  getDocs,
  query,
  where,
  updateDoc,
  setDoc,
} from "firebase/firestore";

const STATUSES = [
  { key: "all", label: "Todos" },
  { key: "pending", label: "Pendientes" },
  { key: "approved", label: "Aprobados" },
  { key: "rejected", label: "Rechazados" },
];

export default function AdminSellers() {
  // Usuario actual (solo visual)
  const [me, setMe] = useState(null);

  // Listado y selección
  const [sellers, setSellers] = useState([]);
  const [selUid, setSelUid] = useState(""); // uid seleccionado

  // Filtros
  const [statusFilter, setStatusFilter] = useState("pending");
  const [search, setSearch] = useState("");

  // Formulario / detalle
  const [form, setForm] = useState({
    uid: "",
    email: "",
    nombreCompleto: "",
    telefono: "",
    rol: "seller",
    sellerStatus: "pending",
    rejectionReason: "",
    docs: {
      ineUrl: "",
      comprobanteDomicilioUrl: "",
    },
    activo: true,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Cargar usuario actual para UI
  useEffect(() => {
    const u = auth.currentUser;
    setMe(u ? { uid: u.uid, email: u.email || "" } : null);
  }, []);

  // Cargar sellers
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // Trae todos los users rol=seller
        const qy = query(collection(db, "users"), where("rol", "==", "seller"));
        const snap = await getDocs(qy);
        const list = snap.docs.map((d) => ({ id: d.id, uid: d.id, ...d.data() }));

        // orden simple por estado y nombre
        list.sort((a, b) => {
          const sa = (a.sellerStatus || "pending").localeCompare(
            b.sellerStatus || "pending"
          );
          if (sa !== 0) return sa;
          return (a.nombre || a.nombreCompleto || a.email || "").localeCompare(
            b.nombre || b.nombreCompleto || b.email || ""
          );
        });

        setSellers(list);

        // preselecciona primero si hay
        if (list.length > 0) {
          const first = list[0];
          setSelUid(first.uid);
          setForm({
            uid: first.uid,
            email: first.email || "",
            nombreCompleto: first.nombre || first.nombreCompleto || "",
            telefono: first.contacto?.telefono || "",
            rol: "seller",
            sellerStatus: first.sellerStatus || "pending",
            rejectionReason: first.rejectionReason || "",
            docs: {
              ineUrl: first.docs?.ineUrl || "",
              comprobanteDomicilioUrl: first.docs?.comprobanteDomicilioUrl || "",
            },
            activo: first.activo !== false,
          });
        } else {
          setSelUid("");
        }
      } catch (e) {
        console.error("[AdminSellers] cargar sellers:", e);
        alert("No se pudo cargar el listado de sellers.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Cambia selección
  useEffect(() => {
    if (!selUid) return;
    const s = sellers.find((x) => x.uid === selUid);
    if (!s) return;
    setForm({
      uid: s.uid,
      email: s.email || "",
      nombreCompleto: s.nombre || s.nombreCompleto || "",
      telefono: s.contacto?.telefono || "",
      rol: "seller",
      sellerStatus: s.sellerStatus || "pending",
      rejectionReason: s.rejectionReason || "",
      docs: {
        ineUrl: s.docs?.ineUrl || "",
        comprobanteDomicilioUrl: s.docs?.comprobanteDomicilioUrl || "",
      },
      activo: s.activo !== false,
    });
  }, [selUid, sellers]);

  const filtered = useMemo(() => {
    let arr = sellers;
    if (statusFilter !== "all") {
      arr = arr.filter((s) => (s.sellerStatus || "pending") === statusFilter);
    }
    const q = search.trim().toLowerCase();
    if (!q) return arr;
    return arr.filter((s) => {
      const str =
        `${s.email || ""} ${s.nombre || ""} ${s.nombreCompleto || ""} ${s.contacto?.telefono || ""}`.toLowerCase();
      return str.includes(q);
    });
  }, [sellers, statusFilter, search]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith("docs.")) {
      const field = name.split(".")[1];
      setForm((v) => ({ ...v, docs: { ...v.docs, [field]: value } }));
      return;
    }
    setForm((v) => ({
      ...v,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const guardarBasico = async () => {
    if (!form.uid) return;
    try {
      setSaving(true);
      const ref = doc(db, "users", form.uid);
      const payload = {
        rol: "seller",
        email: form.email || "",
        nombre: form.nombreCompleto || "",
        activo: !!form.activo,
        sellerStatus: form.sellerStatus || "pending",
        rejectionReason: form.rejectionReason || "",
        contacto: {
          telefono: form.telefono || "",
        },
        docs: {
          ineUrl: form.docs.ineUrl || "",
          comprobanteDomicilioUrl: form.docs.comprobanteDomicilioUrl || "",
        },
        updatedAt: new Date().toISOString(),
      };
      await setDoc(ref, payload, { merge: true });
      alert("Datos guardados.");
    } catch (e) {
      console.error("[AdminSellers] guardar:", e);
      alert(e.message || "No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  };

  const aprobarSeller = async () => {
    if (!form.uid) return;
    try {
      setSaving(true);
      const ref = doc(db, "users", form.uid);
      await updateDoc(ref, {
        sellerStatus: "approved",
        rejectionReason: "",
        activo: true,
        updatedAt: new Date().toISOString(),
      });
      alert("Seller aprobado.");
      // refresca en memoria
      const list = sellers.map((s) =>
        s.uid === form.uid ? { ...s, sellerStatus: "approved", activo: true } : s
      );
      setSellers(list);
      setForm((v) => ({ ...v, sellerStatus: "approved", rejectionReason: "" }));
    } catch (e) {
      console.error("[AdminSellers] aprobar:", e);
      alert(e.message || "No se pudo aprobar.");
    } finally {
      setSaving(false);
    }
  };

  const rechazarSeller = async () => {
    if (!form.uid) return;
    if (!form.rejectionReason.trim()) {
      alert("Indica un motivo de rechazo.");
      return;
    }
    try {
      setSaving(true);
      const ref = doc(db, "users", form.uid);
      await updateDoc(ref, {
        sellerStatus: "rejected",
        rejectionReason: form.rejectionReason.trim(),
        updatedAt: new Date().toISOString(),
      });
      alert("Seller rechazado.");
      const list = sellers.map((s) =>
        s.uid === form.uid
          ? { ...s, sellerStatus: "rejected", rejectionReason: form.rejectionReason.trim() }
          : s
      );
      setSellers(list);
      setForm((v) => ({ ...v, sellerStatus: "rejected" }));
    } catch (e) {
      console.error("[AdminSellers] rechazar:", e);
      alert(e.message || "No se pudo rechazar.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-gray-600">Cargando…</div>;

  return (
    <div className="min-h-screen bg-gray-100 text-gray-900">
      {/* Topbar */}
      <div className="px-6 py-4 border-b border-gray-200 bg-white flex justify-between items-center">
        <div className="font-semibold">Administrar Sellers</div>
        <div className="text-sm text-gray-600">Admin · {me?.email || ""}</div>
      </div>

      <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista */}
        <aside className="lg:col-span-1">
          <div className="rounded border border-gray-200 bg-white p-3 space-y-3">
            <div className="flex items-center gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-2 py-1 rounded border border-gray-300 bg-gray-50"
              >
                {STATUSES.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar…"
                className="flex-1 px-2 py-1 rounded border border-gray-300 bg-gray-50"
              />
            </div>

            <div className="space-y-1 max-h-[60vh] overflow-auto">
              {filtered.map((s) => (
                <button
                  key={s.uid}
                  onClick={() => setSelUid(s.uid)}
                  className={`w-full text-left px-3 py-2 rounded border ${
                    selUid === s.uid ? "border-gray-900 bg-gray-50" : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <div className="font-medium">{s.nombre || s.nombreCompleto || s.email}</div>
                  <div className="text-xs text-gray-500">
                    {s.email} · {s.sellerStatus || "pending"} {s.activo === false ? "· inactivo" : ""}
                  </div>
                </button>
              ))}
              {filtered.length === 0 && (
                <div className="text-sm text-gray-500 px-2 py-3">Sin resultados…</div>
              )}
            </div>
          </div>
        </aside>

        {/* Form */}
        <main className="lg:col-span-2">
          {selUid ? (
            <div className="rounded border border-gray-200 bg-white p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <label className="text-sm text-gray-600">Nombre completo</label>
                  <input
                    name="nombreCompleto"
                    value={form.nombreCompleto}
                    onChange={onChange}
                    className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600">Activo</label>
                  <div className="mt-2">
                    <input
                      type="checkbox"
                      name="activo"
                      checked={!!form.activo}
                      onChange={onChange}
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-sm text-gray-600">Email</label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600">Teléfono/WhatsApp</label>
                  <input
                    name="telefono"
                    value={form.telefono}
                    onChange={onChange}
                    className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600">Estado del seller</label>
                  <select
                    name="sellerStatus"
                    value={form.sellerStatus}
                    onChange={onChange}
                    className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                  >
                    <option value="pending">pending</option>
                    <option value="approved">approved</option>
                    <option value="rejected">rejected</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-600">INE (URL)</label>
                  <input
                    name="docs.ineUrl"
                    value={form.docs.ineUrl}
                    onChange={onChange}
                    className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                    placeholder="https://…"
                  />
                  {form.docs.ineUrl && (
                    <a
                      href={form.docs.ineUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-blue-600 underline mt-1 inline-block"
                    >
                      Ver documento
                    </a>
                  )}
                </div>
                <div>
                  <label className="text-sm text-gray-600">Comprobante domicilio (URL)</label>
                  <input
                    name="docs.comprobanteDomicilioUrl"
                    value={form.docs.comprobanteDomicilioUrl}
                    onChange={onChange}
                    className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                    placeholder="https://…"
                  />
                  {form.docs.comprobanteDomicilioUrl && (
                    <a
                      href={form.docs.comprobanteDomicilioUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-blue-600 underline mt-1 inline-block"
                    >
                      Ver documento
                    </a>
                  )}
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-600">Motivo (si rechaza)</label>
                <textarea
                  name="rejectionReason"
                  rows={2}
                  value={form.rejectionReason}
                  onChange={onChange}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                />
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={guardarBasico}
                  disabled={saving}
                  className="px-4 py-2 rounded bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
                >
                  {saving ? "Guardando…" : "Guardar datos"}
                </button>
                <button
                  onClick={aprobarSeller}
                  disabled={saving || form.sellerStatus === "approved"}
                  className="px-4 py-2 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  Aprobar seller
                </button>
                <button
                  onClick={rechazarSeller}
                  disabled={saving}
                  className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                >
                  Rechazar seller
                </button>

                <span className="text-sm text-gray-600 ml-auto">
                  UID: <code>{form.uid}</code>
                </span>
              </div>
            </div>
          ) : (
            <div className="text-gray-600">Selecciona un seller…</div>
          )}
        </main>
      </div>
    </div>
  );
}